import { AxiosResponse, HttpStatusCode } from 'axios';
import {
  CARRIER_PREFILL_IDENTIFIER_TYPE_PARAM,
  CARRIER_PREFILL_ROUTE,
  CARRIER_PREFILL_TIMEOUT_MS,
  PREFILL_LOOKUP_MARKER_KEY,
} from '../constants/headsUp';
import { CarrierPrefill, PrefillAccountSnapshot } from './carrierPrefill';
import { safeSessionGetItem, safeSessionSetItem } from '../shared/safeStorage';

/**
 * Fetching the Highway prefill (THT-711), shared by the two screens that need it.
 *
 * This lived inside HeadsUp while that page was the only caller. It moved out when Business Info
 * had to be able to ask for the same thing: the prefill is written to sessionStorage, which does
 * not survive a new tab, a restarted browser, or the shared-tab purge — and once the customer has
 * an MC/DOT on record the heads-up page never renders again, so nothing was left to re-fetch it.
 * A customer who reopened signup in a second tab got an empty form and no way back to their data.
 *
 * Deliberately plain functions taking `callProtectedApi` rather than a hook: BusinessInfo calls
 * this from an effect and HeadsUp from a click handler, and a hook would force both into the same
 * shape for no gain.
 */

/** `callProtectedApi` as both callers hold it — typed here so this module needs no React. */
export type ProtectedApiCall = <T = any>(
  url: string,
  requestType: string,
  body?: unknown,
  errorMsg?: string,
  newHeaders?: Record<string, string | undefined>,
  domain?: string,
  retries?: number,
  hideAlert?: boolean
) => Promise<AxiosResponse<T> | null>;

/**
 * The key that identifies a confirmed carrier everywhere downstream — the suggestion record, the
 * stale-drop on a carrier change, and the lookup marker below all agree on this shape.
 */
export const carrierPrefillKey = (
  identifier: string,
  identifierType: string
): string => `${identifierType}:${identifier}`;

/**
 * Best-effort by construction: every failure — non-200, a carrier Highway does not know, a
 * malformed payload, a timeout, a thrown request — resolves to `null`, and a `null` prefill is
 * simply a form the customer fills in themselves. Nothing here is worth a toast or a blocked step.
 *
 * The identifier type is sent explicitly because the customer has told us which carrier is
 * theirs — the backend must not go guessing MC-then-DOT and hand back the other company's details.
 *
 * Deliberately NOT exported: `fetchCarrierPrefillOnce` below is the only way in, so a caller
 * cannot accidentally open a second request for a carrier that already has one open.
 */
const fetchCarrierPrefill = async (
  callProtectedApi: ProtectedApiCall,
  identifier: string,
  identifierType: string
): Promise<CarrierPrefill | null> => {
  const timeout = new Promise<null>((resolve) => {
    setTimeout(() => resolve(null), CARRIER_PREFILL_TIMEOUT_MS);
  });

  const request = (async (): Promise<CarrierPrefill | null> => {
    try {
      const response = await callProtectedApi(
        `${CARRIER_PREFILL_ROUTE}/${identifier}?${CARRIER_PREFILL_IDENTIFIER_TYPE_PARAM}=${identifierType}`,
        'get',
        {},
        undefined,
        undefined,
        undefined,
        0,
        // Best-effort: nothing about a failed prefill is the customer's problem, so no toast.
        true
      );
      if (response?.status !== HttpStatusCode.Ok) return null;
      const data = (response as any)?.data?.data;
      if (!data?.found || !data?.prefill?.legal_name) return null;
      return data.prefill as CarrierPrefill;
    } catch (e: any) {
      console.error('Carrier prefill lookup failed: ', e?.message);
      return null;
    }
  })();

  return Promise.race([request, timeout]);
};

/**
 * The in-flight lookup for a carrier, so the two screens that can both want one share it.
 *
 * They have no other way to see each other: the marker below is only written once a response has
 * landed, and HeadsUp's own guard is a ref that dies with the page. A prefill slower than
 * CARRIER_PREFILL_HANDOFF_GRACE_MS therefore handed off mid-flight, and Business Info — finding no
 * marker — opened a SECOND lookup for the same carrier while the first was still open, and sat
 * behind its loading state for the length of it.
 *
 * Keyed by carrier and dropped the moment the request settles, so this is a window and not a
 * cache: a customer who retries after a miss still gets a fresh call, and the marker keeps doing
 * the across-mounts job it already did. Joining the open request rather than suppressing the
 * second caller is what matters — Business Info still gets the payload, and still fills its form.
 */
const inFlightLookups = new Map<string, Promise<CarrierPrefill | null>>();

/**
 * The carrier most recently asked for, and the answer to "is this response still wanted?".
 *
 * It lives at module scope because the question OUTLIVES THE MOUNT that asked it. The heads-up
 * page is rendered behind a ternary, so handing off unmounts it and the "Not right?" correction
 * mounts a fresh one — with a fresh ref. A guard held on that ref only ever sees its own mount's
 * carrier: mount 1 asks for A, the customer corrects and mount 2 confirms B, and A's late
 * response reads mount 1's ref, still says A, and sails through to overwrite B. Same ending as
 * the in-mount race, reached the long way round, on the correction path this feature added.
 *
 * Every caller asking for a carrier claims it, including one that joins an open request — the
 * claim is "this is what the customer wants now", not "this opened a request".
 */
let latestClaimedCarrier: string | null = null;

/** Whether a response for this carrier is still the one the customer is waiting on. */
export const carrierPrefillIsCurrent = (carrierKey: string): boolean =>
  latestClaimedCarrier === carrierKey;

export const fetchCarrierPrefillOnce = (
  callProtectedApi: ProtectedApiCall,
  identifier: string,
  identifierType: string
): Promise<CarrierPrefill | null> => {
  const key = carrierPrefillKey(identifier, identifierType);
  latestClaimedCarrier = key;
  const inFlight = inFlightLookups.get(key);
  if (inFlight) return inFlight;

  const request = fetchCarrierPrefill(
    callProtectedApi,
    identifier,
    identifierType
  ).finally(() => {
    inFlightLookups.delete(key);
  });
  inFlightLookups.set(key, request);
  return request;
};

/**
 * Drops any lookup still recorded as open. Exists for tests: entries clear themselves the moment
 * a request settles, and in a browser every request settles — `fetchCarrierPrefill` races its own
 * timeout — but a suite that hands the component a promise it never resolves leaves one behind for
 * the next test in the same file to join. Not called from application code.
 */
export const resetCarrierPrefillLookups = () => {
  inFlightLookups.clear();
  latestClaimedCarrier = null;
};

/**
 * The identity the customer actually authenticated with, which is not always on their record.
 *
 * An emailless customer row is a live case in this flow — `signup.tsx` works around one of its
 * consequences already — and without this the account tier below would be empty for those
 * customers, leaving Highway's contact block (routinely a dispatcher or the factoring company)
 * as the best email we had. It is where account notifications go, so it is not a field a registry
 * gets to fill.
 */
export type PrefillAuthIdentity = {
  email?: string;
  phoneNumber?: string;
};

/**
 * What we already know about this customer from our own records. Everything here beats Highway
 * where the two disagree — see `resolveField` in `utils/carrierPrefill.ts`.
 */
export const buildPrefillAccountSnapshot = (
  userRecord: unknown,
  authIdentity: PrefillAuthIdentity = {}
): PrefillAccountSnapshot => {
  const record: any = userRecord ?? {};
  const driver: any = record.driver ?? {};
  return {
    businessName: record.business_name,
    businessPhone: record.phone_number,
    address: {
      address_st_number: record.address_st_number,
      address_st_name: record.address_st_name,
      address_unit: record.address_unit,
      address_city: record.address_city,
      address_state: record.address_state,
      address_zip: record.address_zip,
    },
    // What the customer told us beats the state we would otherwise infer from their address.
    stateOfRegistration: record.state_of_registration,
    firstName: driver.first_name,
    lastName: driver.last_name,
    // The driver record is what Personal Info actually stores; the customer row carries the
    // address they logged in with; Auth0 carries the one they authenticated with when neither
    // of ours does. All three are the ACCOUNT tier — any of them beats a registry contact block.
    email: driver.email || record.email || authIdentity.email,
    phoneNumber: driver.phone_number || authIdentity.phoneNumber,
    // Not a prefill source — carried so the write does not cost BusinessInfo its own factoring
    // restore. See buildBusinessInfoPrefill.
    factoringCompanyName: record.factoring_company_name,
  };
};

/**
 * Which carrier we have already looked up, so the re-fetch on Business Info fires once per
 * carrier rather than on every mount.
 *
 * Written for a MISS as well as a hit, which is the whole point of having it: the suggestion
 * record is only written when a payload actually lands, so a carrier Highway does not know would
 * otherwise be looked up again every single time the step mounted. Living under the heads-up
 * namespace means the logout purge and the shared-tab purge both sweep it, so a genuinely fresh
 * session correctly tries again.
 */
const readSession = (key: string): string | null => safeSessionGetItem(key);

export const carrierAlreadyLookedUp = (carrierKey: string): boolean =>
  readSession(PREFILL_LOOKUP_MARKER_KEY) === carrierKey;

export const markCarrierLookedUp = (carrierKey: string) => {
  // Storage refused; the only cost is a repeated lookup, which is harmless.
  safeSessionSetItem(PREFILL_LOOKUP_MARKER_KEY, carrierKey);
};
